import {useEffect, useRef} from "react";
import PropTypes from "prop-types";
import styled, {css} from "styled-components";
import {FontAwesomeIcon} from "@fortawesome/react-fontawesome";
import {faMagnifyingGlassArrowRight} from "@fortawesome/free-solid-svg-icons";

// Style
import {animateBackground} from "../../style/components/animations.js";
import {border, pad, radius} from "../../style/components/variables.js";
import {flex} from "../../style/components/mixins.js";
import {bp} from "../../style/components/breakpoints.js";
import {
  Button,
  HeadingSmall,
  Inline,
  Loader,
  scrollbar,
  Small,
  Text
} from "../../style/components/general.js";

const hasChecksheetFilter = type => type === "Remind" || type === "Overdue";

// Frame does not scroll (pointer-events off); outer PreviewPanel scrolls.
// Force desktop email layout — device-width viewport meta makes templates look zoomed in a narrow iframe.
const PREVIEW_FRAME_STYLE = `
  html {
    overflow: hidden !important;
    -webkit-text-size-adjust: 100% !important;
    text-size-adjust: 100% !important;
  }
  body {
    overflow: visible !important;
    margin: 0 !important;
    zoom: normal !important;
    transform: none !important;
  }
`;

/** Size iframe to full email content once; outer panel owns scroll. No ResizeObserver (avoids loop errors). */
const fitPreviewFrame = frame => {
  const doc = frame.contentDocument;
  if (!doc?.body) return;

  if (!doc.getElementById("hb-preview-frame-style")) {
    const style = doc.createElement("style");
    style.id = "hb-preview-frame-style";
    style.textContent = PREVIEW_FRAME_STYLE;
    doc.head?.appendChild(style);
  }

  // Neutralize mobile viewport scaling inside the sandboxed email HTML.
  const viewport = doc.querySelector("meta[name=viewport]");
  if (viewport) viewport.setAttribute("content", "width=600, initial-scale=1");

  frame.style.width = "100%";
  frame.style.height = "auto";

  const contentWidth = Math.max(
    doc.documentElement?.scrollWidth || 0,
    doc.body.scrollWidth || 0,
    600
  );
  if (contentWidth > frame.clientWidth) frame.style.width = `${contentWidth}px`;

  const contentHeight = Math.max(
    doc.documentElement?.scrollHeight || 0,
    doc.body.scrollHeight || 0
  );
  if (contentHeight) frame.style.height = `${contentHeight}px`;
};

const PreviewEmailFrame = ({html}) => {
  const frameRef = useRef(null);

  useEffect(() => {
    const onResize = () => {
      if (frameRef.current?.contentDocument?.body) fitPreviewFrame(frameRef.current);
    };
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, [html]);

  return (
    // allow-same-origin keeps styles/images and lets us measure the document; scripts stay blocked.
    <PreviewFrame
      ref={frameRef}
      title="Email preview"
      sandbox="allow-same-origin"
      srcDoc={html}
      onLoad={e => fitPreviewFrame(e.currentTarget)}
    />
  );
};

PreviewEmailFrame.propTypes = {
  html: PropTypes.string.isRequired
};

const PreviewOutline = ({preview, includeTags}) => {
  const {outline, facilities} = preview;

  switch (outline?.kind) {
    case "completed":
      return (
        <PreviewFacility>
          <HeadingSmall>{outline.facility}</HeadingSmall>
          <PreviewRow>
            <Text>{outline.checksheet}</Text>
          </PreviewRow>
          <PreviewRow>
            <Text>Completed by {outline.completedBy}</Text>
          </PreviewRow>
          <PreviewRow>
            <Text>{outline.outOfRange}</Text>
          </PreviewRow>
          {includeTags && (
            <PreviewRow>
              <Text>Includes sample tagged fields</Text>
            </PreviewRow>
          )}
          <PreviewRow>
            <Text>{outline.notes}</Text>
          </PreviewRow>
        </PreviewFacility>
      );
    case "state":
    case "stateRemind":
      return (
        <PreviewFacility>
          <HeadingSmall>{outline.facility}</HeadingSmall>
          <PreviewRow>
            <Text>{outline.message}</Text>
          </PreviewRow>
          {outline.setBy && (
            <PreviewRow>
              <Text>Set by {outline.setBy}</Text>
            </PreviewRow>
          )}
        </PreviewFacility>
      );
    case "componentDisabled":
      return (
        <PreviewFacility>
          <HeadingSmall>{outline.facility}</HeadingSmall>
          {outline.components?.map((comp, idx) => (
            <PreviewRow key={`preview-comp-${idx}-${comp.label}`}>
              <Text>
                {comp.label} — {comp.state}
                {comp.user ? ` (${comp.user})` : ""}
                {comp.explanation ? `: ${comp.explanation}` : ""}
              </Text>
            </PreviewRow>
          ))}
        </PreviewFacility>
      );
    default:
      return (
        facilities?.map(fac => (
          <PreviewFacility key={fac.name}>
            <HeadingSmall>{fac.name}</HeadingSmall>
            {fac.checksheets?.map((sheet, idx) => (
              <PreviewRow key={`preview-sheet-${sheet.id || idx}-${sheet.due || idx}`}>
                <Text>
                  {sheet.checksheet || sheet.name || "Checksheet"}
                  {sheet.due ? ` — due ${sheet.due}` : ""}
                  {sheet.incomplete ? " (incomplete)" : ""}
                </Text>
              </PreviewRow>
            ))}
            {fac.events?.map((event, idx) => (
              <PreviewRow key={`preview-event-${idx}-${event.due || idx}`}>
                <Text>
                  {event.event || event.name || "Event"}
                  {event.due ? ` — due ${event.due}` : ""}
                  {event.incomplete ? " (incomplete)" : ""}
                </Text>
              </PreviewRow>
            ))}
          </PreviewFacility>
        )) || null
      );
  }
};

PreviewOutline.propTypes = {
  preview: PropTypes.objectOf(PropTypes.any).isRequired,
  includeTags: PropTypes.bool.isRequired
};

const NotificationEmailPreview = ({
  preview = null,
  previewLoading,
  previewFailed,
  previewReady,
  previewSubject,
  emptyPreviewKind,
  watchType = null,
  watchFilterChecksheets = false,
  watchChecksheets = [],
  watchIncludeTags = false,
  watchTags = [],
  onExpand,
  variant = "panel"
}) => (
  <PreviewRoot data-testid="notification-email-preview" $variant={variant}>
    {variant === "panel" && (
      <>
        <Inline fullWidth spaced>
          <Text bold>Email Preview</Text>
          {preview?.isSample !== false && <Small>(fake data used for preview)</Small>}
          {onExpand && (
            <Button
              type="button"
              quiet
              onClick={onExpand}
              disabled={!previewReady}
              data-testid="notification-preview-expand"
              aria-label="Expand email preview"
              title="Expand email preview">
              <FontAwesomeIcon icon={faMagnifyingGlassArrowRight} />
            </Button>
          )}
        </Inline>
        {hasChecksheetFilter(watchType) && watchFilterChecksheets && (
          <Small>
            Reports on {watchChecksheets?.length || 0} checksheet
            {(watchChecksheets?.length || 0) === 1 ? "" : "s"}
          </Small>
        )}
        {watchType === "Completed" && watchIncludeTags && (
          <Small>
            Reports on {watchTags?.length || 0} tag
            {(watchTags?.length || 0) === 1 ? "" : "s"}
          </Small>
        )}
      </>
    )}
    <PreviewPanel $variant={variant}>
      <EmailChrome>
        <EmailChromeRow>
          <EmailChromeLabel>From</EmailChromeLabel>
          <Text>Hero Services &lt;support@hero-services.com&gt;</Text>
        </EmailChromeRow>
        <EmailChromeRow>
          <EmailChromeLabel>Subject</EmailChromeLabel>
          <PreviewSubject>{previewSubject}</PreviewSubject>
        </EmailChromeRow>
      </EmailChrome>
      {!previewReady && (
        <PreviewPlaceholder data-testid="notification-preview-placeholder">
          <Small>Select a notification type to preview the email.</Small>
        </PreviewPlaceholder>
      )}
      {/* Avoid empty placeholder while loading — it flashes before the overlay/content. */}
      {previewReady && !preview && !previewLoading && (
        <PreviewPlaceholder data-testid="notification-preview-placeholder">
          {previewFailed && <Small>Unable to load preview.</Small>}
        </PreviewPlaceholder>
      )}
      {preview && (
        <>
          {preview.empty && (
            <Text data-testid="notification-preview-empty">
              No {emptyPreviewKind} items match these filters.
            </Text>
          )}
          {!preview.empty && preview.html && <PreviewEmailFrame html={preview.html} />}
          {!preview.empty && !preview.html && (
            <OutlineFallback data-testid="notification-preview-outline">
              <Small>Mailchimp preview unavailable — showing outline.</Small>
              <PreviewOutline preview={preview} includeTags={!!watchIncludeTags} />
            </OutlineFallback>
          )}
        </>
      )}
      {previewReady && (previewLoading || !preview) && !previewFailed && (
        <PreviewLoadingOverlay
          data-testid="notification-preview-loading"
          aria-live="polite"
          aria-label="Updating preview">
          <Loader />
        </PreviewLoadingOverlay>
      )}
    </PreviewPanel>
  </PreviewRoot>
);

NotificationEmailPreview.propTypes = {
  preview: PropTypes.objectOf(PropTypes.any),
  previewLoading: PropTypes.bool.isRequired,
  previewFailed: PropTypes.bool.isRequired,
  previewReady: PropTypes.bool.isRequired,
  previewSubject: PropTypes.string.isRequired,
  emptyPreviewKind: PropTypes.string.isRequired,
  watchType: PropTypes.string,
  watchFilterChecksheets: PropTypes.bool,
  watchChecksheets: PropTypes.arrayOf(PropTypes.object),
  watchIncludeTags: PropTypes.bool,
  watchTags: PropTypes.arrayOf(PropTypes.object),
  onExpand: PropTypes.func,
  variant: PropTypes.oneOf(["panel", "expanded"])
};

const PreviewRoot = styled.div.withConfig({
  shouldForwardProp: prop => prop !== "$variant"
})`
  flex: 1;
  min-width: 0;
  width: 100%;
  max-width: 100%;
  display: flex;
  flex-direction: column;
  gap: ${pad}px;

  ${({$variant}) =>
    $variant === "panel" &&
    css`
      ${bp(4)} {
        max-width: 520px;
      }
    `}
`;

const PreviewPanel = styled.div.withConfig({
  shouldForwardProp: prop => prop !== "$variant"
})`
  position: relative;
  width: 100%;
  padding: ${pad}px;
  border: ${border} solid ${({theme}) => theme.secondary};
  border-radius: ${radius};
  /* Outer panel owns both axes so the full Mailchimp template stays reachable. */
  overflow: auto;
  ${scrollbar};

  ${({$variant}) =>
    $variant === "panel" &&
    css`
      ${bp(4)} {
        position: sticky;
        top: 0;
        max-height: 60vh;
      }
    `}

  ${({$variant}) =>
    $variant === "expanded" &&
    css`
      max-height: 70vh;
    `}
`;

const PreviewLoadingOverlay = styled.div`
  position: absolute;
  inset: 0;
  z-index: 1;
  border-radius: inherit;
  background: rgba(0, 0, 0, 0.5);
  /* No delay — a deferred fade left an empty panel flash on first type select. */
  animation: ${animateBackground(0, 0.2)} 0.2s linear 1 forwards;
`;

const EmailChrome = styled.div`
  margin-bottom: ${pad}px;
  padding-bottom: ${pad}px;
  border-bottom: ${border} solid ${({theme}) => theme.secondary};
`;

const EmailChromeRow = styled.div`
  display: flex;
  gap: ${pad}px;
  align-items: baseline;
  margin-bottom: ${pad / 2}px;
`;

const EmailChromeLabel = styled(Small)`
  flex: 0 0 64px;
  font-weight: bold;
  text-transform: uppercase;
  opacity: 0.7;
`;

const PreviewSubject = styled(Text)`
  margin: 0;
  font-weight: bold;
`;

const PreviewFrame = styled.iframe`
  display: block;
  width: 100%;
  min-height: 200px;
  border: 0;
  background: #fff;
  border-radius: ${radius};
  /* Let wheel/trackpad scroll the outer preview panel, not the iframe. */
  pointer-events: none;
`;

const PreviewPlaceholder = styled.div`
  position: relative;
  min-height: 240px;
  margin-top: ${pad / 2}px;
  border-radius: ${radius};
  background: ${({theme}) => theme.tertiary};
  border: ${border} dashed ${({theme}) => theme.secondary};
  ${flex("row", "wrap", "center", "center")};
  padding: ${pad * 2}px;

  ${bp(4)} {
    min-height: 420px;
  }
`;

const OutlineFallback = styled.div`
  margin-top: ${pad / 2}px;
`;

const PreviewFacility = styled.div`
  margin: ${pad}px 0;
`;

const PreviewRow = styled.div`
  margin-left: ${pad}px;
  margin-bottom: ${pad / 4}px;
`;

export default NotificationEmailPreview;
